import { geohashEnconde, getGeohashNeighbors, getGeohashRange } from "../../geolocation/geohash";
import { docToModel, mapQuerySnapshotToModel } from "../firestore_model";
import { handleHttpError } from "../../error/errors";
import { emulator } from "settings";
import { app } from "helpers/firebase"
import firebase from "firebase/compat/app";
import "firebase/compat/firestore"
import { getFirestore, collection, query, where, doc, getDoc, orderBy, limit, onSnapshot, getDocs } from "firebase/firestore";


const db = getFirestore(app)

const h3 = require("h3-js");

if (emulator) {
    db.useEmulator("localhost", 8080);
}

const buildFirestoreQueryContext = (collectionPath, conditions = [], options = {}) => {
    const safeConditions = Array.isArray(conditions) ? conditions : [];
    const safeOptions = options && typeof options === 'object' && !Array.isArray(options)
        ? options
        : {};

    return {
        collection: collectionPath || '',
        conditions: safeConditions.map((condition) => {
            if (!condition || typeof condition !== 'object') {
                return String(condition || '');
            }

            if (condition instanceof FireQuery) {
                return {
                    type: 'FireQuery',
                    field: condition.field,
                    operator: condition.op,
                    value: condition.val,
                };
            }

            if (condition instanceof OrderByQuery) {
                return {
                    type: 'OrderByQuery',
                    field: condition.orderField,
                    direction: condition.orderDirection,
                };
            }

            if (condition instanceof LimitQuery) {
                return {
                    type: 'LimitQuery',
                    limit: condition.limit,
                };
            }

            return {
                type: condition.constructor?.name || 'Object',
            };
        }),
        realtime: Boolean(safeOptions.realtime),
        mode: safeOptions.mode === 'changes' ? 'changes' : 'snapshot',
        fetchNew: safeOptions.fetchNew !== undefined ? Boolean(safeOptions.fetchNew) : true,
    };
};

const buildFirestoreDocContext = (collectionPath, docId, options = {}) => {
    const safeOptions = options && typeof options === 'object' && !Array.isArray(options)
        ? options
        : {};

    return {
        collection: collectionPath || '',
        docId: docId || '',
        realtime: Boolean(safeOptions.realtime),
    };
};

const logFirestoreQueryFailure = (error, context = {}) => {
    const errorCode = String(error?.code || '').trim();
    const isPermissionDenied = errorCode === 'permission-denied' || errorCode === 'PERMISSION_DENIED';
    const debugPayload = {
        code: errorCode || 'unknown',
        message: error?.message || '',
        details: error?.details || '',
        context,
    };

    if (typeof console !== 'undefined' && typeof console.error === 'function') {
        console.error('[FirestoreCollection] query failed', debugPayload);
        if (isPermissionDenied && typeof error?.stack === 'string') {
            console.error(error.stack);
        }
    }
};

class QueryCondition {

    apply(queryRef) {
        return query(queryRef)
    }
}

export class FireQuery extends QueryCondition {
    // for Firebase Query conditions
    constructor(field, operator, value, orderBy = null) {
        super();
        this.field = field;
        this.op = operator;
        this.val = value;
        this.orderBy = orderBy; // Optional orderBy field
    }

    apply(queryRef) {
        queryRef = query(queryRef, where(...[this.field, this.op, this.val]));
        // if (this.orderBy) {
        //     queryRef = query(queryRef, orderBy(this.field, this.orderBy));
        // }
        return queryRef;
    }
}

// query(queryRef ,orderBy(orderField, orderDirection))

export class OrderByQuery extends QueryCondition {
    // for Firebase Query conditions
    constructor(orderField, orderDirection) {
        super()
        this.orderField = orderField
        this.orderDirection = orderDirection
    }

    apply(queryRef) {
        return query(queryRef, orderBy(this.orderField, this.orderDirection))
    }
}

export class LimitQuery extends QueryCondition {

    constructor(limit) {
        super();
        this.limit = limit;
    }

    apply(queryRef) {
        return query(queryRef, limit(this.limit))
    }
}

export class GeoQuery extends QueryCondition {
    // TODO: remove this class and its uses
    //  Firebase Geoquery using geoash ranges
    constructor(field, position, radius = 100) {
        super()
        try {
            const geohash_range = getGeohashRange(position.geopoint.latitude, position.geopoint.longitude, radius)

            this.field = field
            this.upper = geohash_range.upper
            this.lower = geohash_range.lower
        } catch (e) {
            console.error("Invalid GeoQuery: " + e.message)
        }

    }
    // f0wt7g9
    apply(queryRef) {
        return query(queryRef, where(...[this.field, ">=", this.lower]), where(...[this.field, "<=", this.upper]))
    }
}

export class H3Query extends QueryCondition {
    //  Firebase Geoquery using geoash ranges
    constructor(field, position, radius = 7) {
        super()
        try {
            switch (radius) {
                case "1":
                    this.field = `${field}.l1`
                    break;
                case "2":
                    this.field = `${field}.l2`
                    break;
                case "3":
                    this.field = `${field}.l3`
                    break;
                case "4":
                    this.field = `${field}.l4`
                    break;
                case "5":
                    this.field = `${field}.l5`
                    break;
                case "6":
                    this.field = `${field}.l6`
                    break;
                case "7":
                    this.field = `${field}.l7`
                    break;
                default:
                    this.field = `${field}.l7`
            }
            const h3Index = h3.geoToH3(position.geopoint.latitude, position.geopoint.longitude, radius);
            const kRing = h3.kRing(h3Index, 1);
            this.kRing = kRing

        } catch (e) {
            console.error("Invalid GeoQuery: " + e.message)
        }

    }
    // f0wt7g9
    apply(queryRef) {
        return query(queryRef, where(...[this.field, "in", this.kRing]))
    }
}

export class QueryDefaultValue {
    constructor(operator=undefined, value) {
        this.operator = operator
        this.value = value
    }
}


/**
 * Thin wrapper around a Firestore collection that keeps query construction,
 * model mapping, and realtime subscriptions in one place.
 *
 * Instances expose a consistent API for fetching a single document (`get`) or
 * a list of documents (`getDocs`) while automatically converting Firestore
 * snapshots into the configured model class.
 */
export default class FirestoreCollection {

    constructor(collection, modelClass = undefined, preConditions = [], postConditions = [])
    {
        this.collection = collection
        this.modelClass = modelClass
        this.preConditions = (Array.isArray(preConditions)) ? preConditions : [];
        this.postConditions = (Array.isArray(postConditions)) ? postConditions : [];
    }   // TODO: change base_conditions with preConditions and post_conditions in order to put the condition before or after the query conditions

    get = (id, onChange, realtime = false) => {
        /*
            Get a document by id pass it to onChange function
            id: document id
            onChange: function to be called when document is fetched
            realtime: if true, onChange will be called everytime the document changes
         */

        // const docRef = db.collection(this.collection).doc(id);
        const docRef = doc(db, this.collection, id);

        if (realtime) {
            let _this = this
            return onSnapshot(docRef, (doc) => {
                onChange(docToModel(doc, _this.modelClass))
            }, (error) => {
                logFirestoreQueryFailure(error, buildFirestoreDocContext(this.collection, id, { realtime: true }));
                handleHttpError(error, { throwError: false, logError: true });
            });
        } else {
            let _this = this
            return getDoc(docRef).then(function (doc) {
                if (doc.exists) {
                    if (onChange) {
                        onChange(docToModel(doc, _this.modelClass))
                    }
                } else {
                    console.log("No such document!");
                }
            }).catch(function (error) {
                logFirestoreQueryFailure(error, buildFirestoreDocContext(_this.collection, id));
                handleHttpError(error, { throwError: false, logError: true });
            });
        }
    }


    query = (conditions) => {
        /*
        similar to quer but now conditions can have a QueryDefaultValue object as value
        if that is the case then alter the condition using the operator and value from QueryDefaultValue
        another requirement is that query should keep the same order from start, if after a conditions all the next conditions have QueryDefaultValue then we can skip them, but we cannot skip any condition and then include the next ones
        example if I have conditions a,b,c,d  I can have a or a,b or a,b,c or a,b,c,d but I cannot have a,c or a,d or a,b,d
        my hint is that we first need to process the conditions in reverse direction and then apply them in reverse order
        */
        // TODO: add support adding order in FireQuery directly and it adds the orderby query condition just after the FireQuery condition

        let ColRef = collection(db, this.collection);
        let queryRef = query(ColRef);
        conditions = [...(this.preConditions || []), ...conditions];
        conditions = [...conditions, ...(this.postConditions || [])];

        let valid_conditions = [];
        let skip_condition = true;

        // Reverse loop to process QueryDefaultValue objects
        for (let condition of conditions.reverse()) {
            if (condition instanceof FireQuery) {
                if (condition.val instanceof QueryDefaultValue) {
                    if (!skip_condition) {
                        if (condition.val.operator !== undefined) {
                            condition.op = condition.val.operator;
                        }
                        if (condition.val.value !== undefined) {
                            condition.val = condition.val.value;
                        }
                        // FireQuery with QueryDefaultValue
                        valid_conditions.push(condition);
                    }
                    // skip condition
                }else if (![undefined, null, ""].includes(condition.val)) {
                    skip_condition = false;
                    valid_conditions.push(condition);
                }
            }else{
                // if not a FireQuery
                valid_conditions.push(condition);
            }
        }

        // Forward loop to apply valid conditions
        for (let condition of valid_conditions.reverse()) {
            queryRef = condition.apply(queryRef);
        }

        return queryRef;
    }

    applyOrderBys = (queryRef, orderBys = []) => {
        /*
        Apply order bys to a collection reference
        OrderBys can be a string or an array of string or an array of array [field, direction]
        */
        let _orderBys = []
        if (typeof orderBys === 'string' || orderBys instanceof String) {
            _orderBys.push([orderBys, 'desc'])
        }
        if (Array.isArray(orderBys)) {
            _orderBys = orderBys
        }
        for (let _orderBy of _orderBys) {
            const orderField = Array.isArray(_orderBy) ? _orderBy[0] : _orderBy
            const orderDirection = _orderBy[1]
            if (orderField !== undefined) {
                queryRef =  query(queryRef ,orderBy(orderField, orderDirection))
            }
        }
        return queryRef
    }

    normaliseGetDocsOptions = (options = {}) => {
        const safeOptions = options && typeof options === 'object' && !Array.isArray(options)
            ? options
            : {};

        return {
            realtime: Boolean(safeOptions.realtime),
            mode: safeOptions.mode === 'changes' ? 'changes' : 'snapshot',
            fetchNew: safeOptions.fetchNew !== undefined ? Boolean(safeOptions.fetchNew) : true,
        }
    }

    processQuerySnapshot = (querySnapshot) => {
        return mapQuerySnapshotToModel(querySnapshot, this.modelClass)
    }

    processRealtimeDocChanges = (querySnapshot, existingDocIds = new Set(), fetchNew = true) => {
        const results = [];

        querySnapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
                if (fetchNew || existingDocIds.has(change.doc.id)) {
                    results.push(docToModel(change.doc, this.modelClass));
                }
                existingDocIds.add(change.doc.id);
            } else if (change.type === "modified" && existingDocIds.has(change.doc.id)) {
                results.push(docToModel(change.doc, this.modelClass));
            } else if (change.type === "removed") {
                existingDocIds.delete(change.doc.id);
            }
        });

        return results;
    }

    /**
     * Fetches or subscribes to documents that match the provided query conditions.
     *
     * Pass an options object as the third argument:
     * `getDocs(conditions, onChange, { realtime: true })`
     *
     * Realtime modes:
     * - `snapshot` (default): delivers the full current result set on every update.
     * - `changes`: delivers only changed documents for each snapshot event.
     *
     * `fetchNew` applies to changes-mode realtime subscriptions and controls
     * whether the initial set of `added` documents is emitted.
     */
    getDocs = (conditions, onChange = undefined, options = {}) => {
        let queryRef = this.query(conditions);
        let existingDocIds = new Set();
        const { realtime, mode, fetchNew: shouldFetchNew } = this.normaliseGetDocsOptions(options)
        const queryContext = buildFirestoreQueryContext(this.collection, conditions, { realtime, mode, fetchNew: shouldFetchNew });

        if (realtime) {
            return onSnapshot(queryRef, (querySnapshot) => {
                const results = mode === 'snapshot'
                    ? this.processQuerySnapshot(querySnapshot)
                    : this.processRealtimeDocChanges(querySnapshot, existingDocIds, shouldFetchNew);
                if (onChange) {
                    onChange(results);
                }
            }, (error) => {
                logFirestoreQueryFailure(error, queryContext);
                handleHttpError(error, { throwError: false, logError: true });
            });
        } else {
            return getDocs(queryRef)
              .then((querySnapshot) => {
                  const results = this.processQuerySnapshot(querySnapshot);
                  if (onChange) {
                      onChange(results);
                  }
              })
              .catch((error) => {
                  logFirestoreQueryFailure(error, queryContext);
                  handleHttpError(error, { throwError: true, logError: true });
              });
        }
    };


    getDocs_ = (conditions, onChange = undefined, realtime = false) => {
        return this.getDocs(conditions, onChange, {
            realtime,
            mode: 'snapshot',
        })
    }
}
